import { useZero } from "@rocicorp/zero/react";
import { useLayoutEffect, useRef } from "react";
import { configurationPatch } from "../../../shared/connectors/configurationPatch";
import {
  listFilterKey,
  listRowKey,
  listValues,
} from "../../../shared/connectors/listRows";
import type {
  ListField,
  ListFieldOption,
} from "../../../shared/connectors/types";
import { sharedMutators } from "../../../shared/mutators";
import { config } from "../../environment";
import { ClickButton } from "../ClickButton";
import { SettingsCard, SettingsHeading, SettingsRow } from "../SettingsCard";
import { Text } from "../Text";
import { ToggleVisual } from "../Toggle";
import { AllowListPicker } from "./AllowListPicker";
import type { App } from "./Form";

type Props = {
  item: ListField & { options: readonly ListFieldOption[] };
  app: App;
  disabled?: boolean;
  onEdit?: () => void;
};

export function ModelAccessField({ item, app, disabled, onEdit }: Props) {
  const z = useZero();
  const column = item.columns[0]!.field;
  const filterKey = listFilterKey(item.prefix);
  const limited = app.configuration[filterKey] === "enabled";
  const picked = new Set(listValues(app.configuration, item.prefix));

  // Toggling several models before Zero re-renders must build on the previous
  // write rather than the render-time snapshot, or a later pick drops an
  // earlier one.
  const configRef = useRef(app.configuration);
  useLayoutEffect(() => {
    configRef.current = app.configuration;
  }, [app.configuration]);

  const write = (after: Record<string, string>) => {
    const before = configRef.current;
    configRef.current = after;
    onEdit?.();
    z.mutate(
      sharedMutators.updateConnectedApp({
        connectedAppId: app.id,
        configurationPatch: configurationPatch(before, after),
      })
    );
  };

  const rowKey = new RegExp(`^${item.prefix}\\.\\d+\\.`);
  const withPicks = (picks: ReadonlySet<string>) => ({
    ...Object.fromEntries(
      Object.entries(configRef.current).filter(([key]) => !rowKey.test(key))
    ),
    ...Object.fromEntries(
      item.options
        .filter((option) => picks.has(option.name))
        .map((option, index) => [
          listRowKey(item.prefix, index, column),
          option.name,
        ])
    ),
  });

  const toggle = (option: string) => {
    const next = new Set(listValues(configRef.current, item.prefix));
    if (next.has(option)) next.delete(option);
    else next.add(option);
    write(withPicks(next));
  };

  const toggleLimited = () => {
    const { [filterKey]: _, ...rest } = configRef.current;
    write(limited ? rest : { ...rest, [filterKey]: "enabled" });
  };

  return (
    <SettingsCard
      role="region"
      aria-label="Model access"
      style={{ marginTop: 16 }}
    >
      <SettingsRow>
        <SettingsHeading>
          <Text variant="text-regular-md">{item.label}</Text>
          {item.helperText && (
            <Text variant="text-small" color="var(--text-secondary)">
              {item.helperText}
            </Text>
          )}
        </SettingsHeading>
        <ClickButton
          type="button"
          role="switch"
          aria-label={item.label}
          aria-checked={limited}
          disabled={disabled}
          style={{ flexShrink: 0 }}
          onClick={toggleLimited}
        >
          <ToggleVisual isOn={limited} />
        </ClickButton>
      </SettingsRow>
      {limited && (
        <AllowListPicker
          items={item.options.filter((option) =>
            config.managedAiConnectorModels.includes(option.name)
          )}
          noun={{ singular: "model", plural: "models" }}
          icon="sparkles"
          selected={picked}
          disabled={disabled}
          onToggle={toggle}
        />
      )}
    </SettingsCard>
  );
}
